import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { analyticsAPI, projectsAPI, reportsAPI } from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import { Link } from 'react-router-dom';
import {
  FolderKanban,
  FileText,
  Upload,
  Clock,
  CheckCircle2,
  AlertTriangle,
  ArrowRight,
  MapPin
} from 'lucide-react';

export default function AgentDashboard() {
  const { user } = useAuth();
  const [analytics, setAnalytics] = useState(null);
  const [projects, setProjects] = useState([]);
  const [recentReports, setRecentReports] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [analyticsRes, projectsRes, reportsRes] = await Promise.all([
        analyticsAPI.overview(),
        projectsAPI.list(),
        reportsAPI.list()
      ]);
      setAnalytics(analyticsRes.data);
      setProjects(projectsRes.data);
      setRecentReports(reportsRes.data.slice(0, 5));
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="spinner"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6 page-enter" data-testid="agent-dashboard">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl lg:text-3xl font-bold text-slate-900 font-['Manrope']">
            Welcome, {user?.name?.split(' ')[0]}
          </h1>
          <p className="text-slate-500 mt-1">Here are your assigned projects and tasks</p>
        </div>
        <Button asChild size="lg" className="bg-emerald-600 hover:bg-emerald-700" data-testid="submit-report-btn">
          <Link to="/agent/submit-report">
            <Upload className="w-5 h-5 mr-2" />
            Submit Report
          </Link>
        </Button>
      </div>

      {/* Quick Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Card className="stat-card">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-100 rounded-lg">
                <FolderKanban className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-900 font-['Manrope']">
                  {analytics?.total_projects || 0}
                </p>
                <p className="text-sm text-slate-500">Assigned Projects</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="stat-card">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-slate-100 rounded-lg">
                <FileText className="w-5 h-5 text-slate-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-900 font-['Manrope']">
                  {analytics?.total_reports || 0}
                </p>
                <p className="text-sm text-slate-500">Reports Submitted</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="stat-card">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-emerald-100 rounded-lg">
                <CheckCircle2 className="w-5 h-5 text-emerald-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-emerald-600 font-['Manrope']">
                  {analytics?.verified_reports || 0}
                </p>
                <p className="text-sm text-slate-500">Verified</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="stat-card">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-amber-100 rounded-lg">
                <Clock className="w-5 h-5 text-amber-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-amber-600 font-['Manrope']">
                  {analytics?.pending_reports || 0}
                </p>
                <p className="text-sm text-slate-500">Pending Review</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Assigned Projects */}
      <Card className="stat-card">
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="text-lg font-semibold font-['Manrope']">Your Projects</CardTitle>
          <Button variant="ghost" size="sm" asChild>
            <Link to="/agent/projects">
              View All
              <ArrowRight className="w-4 h-4 ml-1" />
            </Link>
          </Button>
        </CardHeader>
        <CardContent>
          {projects.length > 0 ? (
            <div className="space-y-4">
              {projects.slice(0, 5).map((project) => (
                <div
                  key={project.project_id}
                  className="flex items-center justify-between p-4 bg-slate-50 rounded-lg hover:bg-slate-100 transition-colors"
                  data-testid={`project-${project.project_id}`}
                >
                  <div className="flex-1">
                    <h4 className="font-medium text-slate-900">{project.title}</h4>
                    <div className="flex items-center gap-4 mt-1 text-sm text-slate-500">
                      {(project.country || project.region) && (
                        <span className="flex items-center gap-1">
                          <MapPin className="w-3 h-3" />
                          {project.country}{project.region && `, ${project.region}`}
                        </span>
                      )}
                      <span className={`status-badge ${project.status}`}>{project.status}</span>
                    </div>
                    {/* Progress bar */}
                    <div className="mt-2">
                      <div className="flex items-center justify-between text-xs mb-1">
                        <span className="text-slate-500">Progress</span>
                        <span className="font-medium">{project.progress}%</span>
                      </div>
                      <div className="progress-bar h-1.5">
                        <div className="progress-bar-fill" style={{ width: `${project.progress}%` }}></div>
                      </div>
                    </div>
                  </div>
                  <Button variant="ghost" size="sm" asChild>
                    <Link to={`/agent/projects/${project.project_id}`}>
                      View
                    </Link>
                  </Button>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8 text-slate-400">
              <FolderKanban className="w-12 h-12 mx-auto mb-2" />
              <p>No projects assigned yet</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Recent Reports */}
      <Card className="stat-card">
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="text-lg font-semibold font-['Manrope']">Recent Reports</CardTitle>
          <Button variant="ghost" size="sm" asChild>
            <Link to="/agent/reports">
              View All
              <ArrowRight className="w-4 h-4 ml-1" />
            </Link>
          </Button>
        </CardHeader>
        <CardContent>
          {recentReports.length > 0 ? (
            <div className="space-y-3">
              {recentReports.map((report) => (
                <div
                  key={report.report_id}
                  className="flex items-center justify-between p-3 rounded-lg border border-slate-100"
                  data-testid={`report-${report.report_id}`}
                >
                  <div className="flex items-center gap-3">
                    {report.status === 'verified' ? (
                      <CheckCircle2 className="w-5 h-5 text-emerald-500" />
                    ) : report.status === 'flagged' ? (
                      <AlertTriangle className="w-5 h-5 text-red-500" />
                    ) : (
                      <Clock className="w-5 h-5 text-amber-500" />
                    )}
                    <div>
                      <p className="font-medium text-slate-900 text-sm">{report.title}</p>
                      <p className="text-xs text-slate-500">
                        {new Date(report.created_at).toLocaleDateString()}
                      </p>
                    </div>
                  </div>
                  <span className={`status-badge ${report.status}`}>{report.status}</span>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8 text-slate-400">
              <FileText className="w-12 h-12 mx-auto mb-2" />
              <p>No reports submitted yet</p>
              <Button className="mt-4" asChild>
                <Link to="/agent/submit-report">
                  <Upload className="w-4 h-4 mr-2" />
                  Submit Your First Report
                </Link>
              </Button>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
